import { 
  WebSocketMessage, 
  ConnectionStatus, 
  MessageHandlers,
  ErrorCode,
  ErrorMessage 
} from '@speech-translator/shared';
import { API_CONFIG } from '@/config/api';

export interface WebSocketClientOptions {
  url: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  reconnectBackoffMultiplier?: number;
  maxReconnectInterval?: number;
  pingInterval?: number;
  pongTimeout?: number;
}

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private reconnectBackoffMultiplier: number;
  private maxReconnectInterval: number;
  private pingInterval: number;
  private pongTimeout: number;
  
  private reconnectAttempts = 0;
  private reconnectTimeoutId: number | null = null;
  private pingIntervalId: number | null = null;
  private pongTimeoutId: number | null = null;
  private isManualClose = false;
  
  private status: ConnectionStatus = 'disconnected';
  private messageHandlers: MessageHandlers = {};
  private statusHandlers: Array<(status: ConnectionStatus) => void> = [];
  
  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.reconnectInterval = options.reconnectInterval ?? 1000;
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 5;
    this.reconnectBackoffMultiplier = options.reconnectBackoffMultiplier ?? 1.5;
    this.maxReconnectInterval = options.maxReconnectInterval ?? 30000;
    this.pingInterval = options.pingInterval ?? 30000;
    this.pongTimeout = options.pongTimeout ?? 5000;
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      this.isManualClose = false;
      this.setStatus('connecting');
      
      try {
        this.ws = new WebSocket(this.url);
        
        this.ws.onopen = () => {
          this.reconnectAttempts = 0;
          this.setStatus('connected');
          this.startPingPong();
          resolve();
        };
        
        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };
        
        this.ws.onclose = (_event) => {
          this.stopPingPong();
          
          if (!this.isManualClose) {
            this.setStatus('disconnected');
            this.scheduleReconnect();
          }
        };
        
        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.setStatus('error');
          
          if (this.reconnectAttempts === 0) {
            reject(new Error('Failed to establish WebSocket connection'));
          }
        };
        
      } catch (error) {
        this.setStatus('error');
        reject(error);
      }
    });
  }

  public disconnect(): void {
    this.isManualClose = true;
    this.clearReconnectTimeout();
    this.stopPingPong();
    
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    
    this.setStatus('disconnected');
  }

  public send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (message.type === 'audio_data') {
        console.log(`Sending WebSocket message: ${message.type}, sequence: ${(message as any).data.sequence}`);
      } else {
        console.log(`Sending WebSocket message: ${message.type}`);
      }
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
      this.emitError(ErrorCode.CONNECTION_ERROR, 'WebSocket is not connected');
    }
  }

  public onMessage<T extends WebSocketMessage['type']>(
    type: T, 
    handler: MessageHandlers[T]
  ): void {
    this.messageHandlers[type] = handler;
  }

  public onStatusChange(handler: (status: ConnectionStatus) => void): void {
    this.statusHandlers.push(handler);
  }

  public getStatus(): ConnectionStatus {
    return this.status;
  }

  public isConnected(): boolean {
    return this.status === 'connected' && this.ws?.readyState === WebSocket.OPEN;
  }

  private handleMessage(data: string): void {
    try {
      const message: WebSocketMessage = JSON.parse(data);
      
      // Handle pong messages for keep-alive
      if (data === 'pong') {
        this.clearPongTimeout();
        return;
      }
      
      // Validate message structure
      if (!message.type || !message.timestamp) {
        this.emitError(ErrorCode.INVALID_MESSAGE_FORMAT, 'Invalid message format');
        return;
      }
      
      const handler = this.messageHandlers[message.type];
      if (handler) {
        handler(message as any);
      }
      
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
      this.emitError(ErrorCode.INVALID_MESSAGE_FORMAT, 'Failed to parse message');
    }
  }

  private scheduleReconnect(): void {
    if (this.isManualClose || this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.setStatus('error');
      return;
    }

    this.setStatus('reconnecting');
    
    const delay = Math.min(
      this.reconnectInterval * Math.pow(this.reconnectBackoffMultiplier, this.reconnectAttempts),
      this.maxReconnectInterval
    );
    
    this.reconnectTimeoutId = window.setTimeout(() => {
      this.reconnectAttempts++;
      this.connect().catch(() => {
        // Error handling is done in connect method
      });
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  private startPingPong(): void {
    this.pingIntervalId = window.setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
        this.setPongTimeout();
      }
    }, this.pingInterval);
  }

  private stopPingPong(): void {
    if (this.pingIntervalId) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
    this.clearPongTimeout();
  }

  private setPongTimeout(): void {
    this.pongTimeoutId = window.setTimeout(() => {
      console.warn('Pong timeout - connection may be stale');
      this.ws?.close(1000, 'Pong timeout');
    }, this.pongTimeout);
  }

  private clearPongTimeout(): void {
    if (this.pongTimeoutId) {
      clearTimeout(this.pongTimeoutId);
      this.pongTimeoutId = null;
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status !== status) {
      this.status = status;
      this.statusHandlers.forEach(handler => handler(status));
    }
  }

  private emitError(code: ErrorCode, message: string, details?: any): void {
    const errorMessage: ErrorMessage = {
      type: 'error',
      timestamp: Date.now(),
      data: {
        code,
        message,
        details
      }
    };
    
    const handler = this.messageHandlers.error;
    if (handler) {
      handler(errorMessage);
    }
  }
}

// Factory function for creating WebSocket clients
export function createWebSocketClient(roomId: string, role: 'speaker' | 'listener', language?: string): WebSocketClient {
  // Use the configured WebSocket URL instead of current location
  const baseWsUrl = API_CONFIG.WEBSOCKET_URL;
  
  let url = `${baseWsUrl}/ws/${roomId}?role=${role}`;
  if (language && role === 'listener') {
    url += `&language=${language}`;
  }
  
  return new WebSocketClient({
    url,
    reconnectInterval: 1000,
    maxReconnectAttempts: 5,
    reconnectBackoffMultiplier: 1.5,
    maxReconnectInterval: 30000,
    pingInterval: 30000,
    pongTimeout: 5000
  });
}